/* ═══════════════════════════════════════════════════════════════
   Details widget (Project Canvas R1, T2) — the project's identity
   fields (number, status, name, client, type, PIC, PM) plus
   Scopes as a chips row at the end of the same Fields grid — moved
   verbatim out of the old ProjectPanel.tsx chrome into a stock widget
   body. Same fields, same SearchableSelect editors, same Manage
   project Sheet, same Close-project confirm gate (Status → Closed),
   same permissions (`ctx.can.edit`, `ctx.can.archiveProject`).

   Per-section collapse/reorder (the old PanelSection wrapper) retired
   (Decision 12) — the widget grid's own move/hide replaces it. The
   header's Manage action is now the kit's icon-only WidgetActionButton
   (every other stock widget's convention) with the SAME label, "Manage
   project", carried as its aria-label/tooltip.
   ═══════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { AlertTriangle, Settings2 } from 'lucide-react';
import type { Project } from '../../../../shared/entities';
import { Fields, WidgetFrame, WidgetActionButton } from '../../../components/widgets';
import type { FieldDef } from '../../../components/widgets';
import { Icon } from '../../../components/ui/Icon';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { sanitizeProjectName } from '../helpers';
import { ProjectFormPanel } from '../ProjectFormPanel';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

function SectionLoading() {
  return (
    <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }} role="status">
      Loading…
    </div>
  );
}

function DetailsBody({
  ctx,
  onRequestClose,
}: {
  ctx: ProjectWidgetCtx;
  onRequestClose: () => void;
}) {
  const p = ctx.project;
  const canEdit = ctx.can.edit;

  const client = ctx.orgs.find((o) => o.id === p.clientOrgId)?.name || p.clientName || '–';
  const type = ctx.projectTypes.find((t) => t.id === p.projectTypeId)?.name || '–';
  const pic = ctx.members.find((m) => m.id === p.picId)?.name || '–';
  const pm = ctx.members.find((m) => m.id === p.pmId)?.name || '–';

  const save = (patch: Partial<Project>) =>
    ctx.saveProject({ ...p, ...patch }).then(() => undefined);

  const noop = async () => {};

  const fields: FieldDef[] = [
    {
      key: 'projectNumber',
      label: 'Project number',
      value: p.projectNumber || null,
      canEdit,
      onSave: (v) => save({ projectNumber: v }),
    },
    {
      key: 'status',
      label: 'Status',
      value: p.status || 'active',
      display: (p.status || 'active').charAt(0).toUpperCase() + (p.status || 'active').slice(1),
      editor: {
        kind: 'select',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'closed', label: 'Closed' },
        ],
      },
      // Closing is the only status change possible on an active project
      // (reopen is header-only), and it requires `projects.archive` — an
      // `edit`-only holder must not be offered the control at all, even
      // though the server would refuse the write with 403.
      canEdit: ctx.can.archiveProject && !ctx.locked,
      // Picking Closed never saves directly — it opens the same confirm
      // the header's "Close project" action uses (Josh: Status → Closed
      // won't save was the R1 bug; a silent write would be worse).
      onSave: (v) => {
        if (v === 'closed') {
          onRequestClose();
          return Promise.resolve();
        }
        return save({ status: v as 'active' | 'closed' });
      },
    },
    {
      key: 'name',
      label: 'Project name',
      value: p.name,
      display: sanitizeProjectName(p.name) || 'Untitled',
      canEdit,
      onSave: (v) =>
        v.trim() ? save({ name: v.trim() }) : Promise.reject(new Error('Name is required')),
    },
    {
      key: 'client',
      label: 'Client',
      value: p.clientOrgId || null,
      display: client,
      editor: { kind: 'select', options: ctx.orgs.map((o) => ({ value: o.id, label: o.name })) },
      canEdit,
      onSave: (v) =>
        save({ clientOrgId: v || undefined, clientName: ctx.orgs.find((o) => o.id === v)?.name }),
    },
    {
      key: 'type',
      label: 'Type',
      value: p.projectTypeId || null,
      display: type,
      editor: {
        kind: 'select',
        options: ctx.projectTypes.map((t) => ({ value: t.id, label: t.name })),
      },
      canEdit,
      onSave: (v) => save({ projectTypeId: v || undefined }),
    },
    {
      key: 'pic',
      label: 'PIC',
      value: p.picId || null,
      display: pic,
      editor: {
        kind: 'select',
        options: ctx.members.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name })),
      },
      canEdit,
      onSave: (v) => save({ picId: v || undefined }),
    },
    {
      key: 'pm',
      label: 'PM',
      value: p.pmId || null,
      display: pm,
      editor: {
        kind: 'select',
        options: ctx.members.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name })),
      },
      canEdit,
      onSave: (v) => save({ pmId: v || undefined }),
    },
  ];

  // Scopes — merged into Details as its last full-width row (Josh
  // 2026-09-30: "if scopes is part of the details, then remove the line
  // between them, and title the section just details"), no rule/heading
  // between it and the fields above.
  const scopeAssignments = ctx.scopeAssignments.data;
  const scopeChips = Array.from(new Map(scopeAssignments.map((a) => [a.scopeId, a])).values())
    .map((a) => ctx.allScopes.find((s) => s.id === a.scopeId))
    .filter((s): s is NonNullable<typeof s> => !!s);

  let scopesDisplay: React.ReactNode;
  if (ctx.scopeAssignments.status === 'loading') {
    scopesDisplay = <SectionLoading />;
  } else if (ctx.scopeAssignments.status === 'error') {
    scopesDisplay = (
      <EmptyState
        compact
        icon={<Icon icon={AlertTriangle} em={1.75} className="text-danger" />}
        title={ctx.scopeAssignments.error || 'Could not load scopes.'}
        action={{ label: 'Try again', onClick: () => void ctx.scopeAssignments.reload() }}
      />
    );
  } else if (scopeChips.length === 0) {
    scopesDisplay = <EmptyState compact title="No scopes assigned" />;
  } else {
    scopesDisplay = (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(6) }}>
        {scopeChips.map((s) => (
          <span
            key={s.id}
            style={{
              fontSize: 'var(--font-size-sm)',
              padding: `${rem(3)} ${rem(10)}`,
              borderRadius: 'var(--radius-sm)',
              background: s.color + '18',
              color: s.color,
              fontWeight: 600,
            }}
          >
            {s.name}
          </span>
        ))}
      </div>
    );
  }

  fields.push({
    key: 'scopes',
    label: 'Scopes',
    value: null,
    display: scopesDisplay,
    canEdit: false,
    onSave: noop,
    span: 'row',
  });

  return <Fields fields={fields} density="compact" />;
}

function DetailsCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [manageProjectOpen, setManageProjectOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const p = ctx.project;

  const confirmCloseProject = async () => {
    if (!ctx.can.archiveProject) return;
    setClosing(true);
    try {
      await ctx.saveProject({ ...p, status: 'closed' });
      ctx.toast('Project closed');
      setConfirmClose(false);
    } catch (err: any) {
      ctx.toast(err?.message || 'Something went wrong', true);
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <WidgetFrame
        title="Details"
        status="ready"
        form="fields"
        actions={
          ctx.can.edit ? (
            <WidgetActionButton
              icon={Settings2}
              label="Manage project"
              onClick={() => setManageProjectOpen(true)}
            />
          ) : undefined
        }
      >
        <DetailsBody ctx={ctx} onRequestClose={() => setConfirmClose(true)} />
      </WidgetFrame>

      <ProjectFormPanel
        open={manageProjectOpen}
        mode="edit"
        project={p}
        orgs={ctx.orgs}
        projectTypes={ctx.projectTypes}
        billingTypes={ctx.billingTypes}
        members={ctx.members}
        firmPhases={ctx.firmPhases}
        allScopes={ctx.allScopes}
        scopeAssignments={ctx.scopeAssignments.data}
        phases={ctx.phases.data}
        onSave={(next) => ctx.saveProject(next)}
        onSaved={() => {}}
        canViewFinancials={ctx.can.viewFinancials}
        canArchiveProject={ctx.can.archiveProject}
        onClose={() => {
          setManageProjectOpen(false);
          void ctx.scopeAssignments.reload();
        }}
        onPhaseCommitted={(patch) => {
          if (patch.currentPhaseId) void ctx.setCurrentPhase(patch.currentPhaseId);
        }}
        toast={ctx.toast}
      />

      <ConfirmDialog
        open={confirmClose}
        onOpenChange={(o) => !o && !closing && setConfirmClose(false)}
        title="Close this project?"
        description="Every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice on this project becomes read-only. Only Reopen can change it."
        confirmLabel="Close project"
        destructive
        busy={closing}
        onConfirm={confirmCloseProject}
      />
    </>
  );
}

export const detailsWidget: ProjectWidgetDef = {
  id: 'project_details',
  title: 'Details',
  description: 'Project number, status, name, client, type, PIC, PM and scopes.',
  category: 'overview',
  cols: { min: 6, max: 12 },
  defaultSize: 'standard',
  inputSurface: true,
  render: (ctx) => <DetailsCard ctx={ctx} />,
};
